import type { ElementType } from 'react';
import './chassis.css';
import { cn } from './cn';

export interface WordmarkProps {
  /**
   * Sub-brand suffix — "men", "tv", "vip", "records".
   *
   * `.app` is the primary consumer surface and does not suffix itself, so
   * passing "app" (or nothing) renders the bare `HOTTR.` lockup.
   */
  suffix?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'hero';
  as?: ElementType;
  className?: string;
}

const SIZE: Record<NonNullable<WordmarkProps['size']>, string> = {
  sm: 'text-[12px]',
  md: 'text-[14px]',
  lg: 'text-[18px]',
  xl: 'text-[24px]',
  hero: 'text-[clamp(2.5rem,6vw,4.5rem)]',
};

/**
 * The HOTTR. wordmark — Neon Noir v2.3.
 *
 * Construction is fixed: HOTTR (Poppins 900, uppercase, -0.04em) + a flat pink
 * fullstop + the suffix locked tight against it. The fullstop is the brand's
 * iconic atom and is never glowed inside the wordmark.
 *
 * v2.3 corrected the suffix to weight 300 roman at 70% opacity — `.men` was
 * already right and `.app` was still rendering suffixes at 900. This is the
 * single correct construction for every surface.
 *
 * ── Where the construction lives, and why it is not Tailwind ───────────────
 *
 * Everything except SIZE is `.hottr-wordmark*` in chassis.css. That is not
 * stylistic preference — `.music`, `.tv` and `.chat` do not load the shared
 * Tailwind preset, so `font-display` / `tracking-hottr-display` /
 * `text-hottr-pink` do not exist in those builds and the lockup would render
 * unstyled on a third of the estate. chassis.css ships to all nine apps and
 * carries its own colour fallbacks, so the wordmark cannot be broken by a
 * consumer's build config being behind.
 *
 * Size stays here because arbitrary values (`text-[20px]`) need no preset.
 *
 * Two failures this replaces, both live before now:
 *
 * 1. The suffix carried `className="hottr-suffix"`. That rule is real — the
 *    Tailwind preset adds it — but only six of the nine apps load the preset,
 *    so on `.music`, `.tv` and `.chat` the class matched nothing and the suffix
 *    inherited the parent's 900 uppercase, rendering `HOTTR.CARE`. A brand rule
 *    that depends on a build config is a brand rule a third of the estate does
 *    not have.
 * 2. `.hottr-lockup` in chassis.css carried `gap: 5px`, and WorldSwitcher drew
 *    its own spans against it rather than using this component — so the
 *    switcher rendered `HOTTR . care` on every site that used it.
 */
export const Wordmark = ({ suffix, size = 'md', as: Tag = 'span', className }: WordmarkProps) => {
  const raw = suffix?.replace(/^\./, '').toLowerCase();
  const shown = raw && raw !== 'app' ? raw : '';

  return (
    <Tag className={cn('hottr-wordmark', SIZE[size], className)} aria-label={`HOTTR.${shown}`}>
      <span aria-hidden className="hottr-wordmark__main">
        HOTTR
      </span>
      <span aria-hidden className="hottr-wordmark__dot">
        .
      </span>
      {shown && (
        <span aria-hidden className="hottr-wordmark__suffix">
          {shown}
        </span>
      )}
    </Tag>
  );
};

/**
 * Pink fullstop to close a brand statement line.
 *
 * Uses the same class as the wordmark's own dot, for the same reason: it must
 * render pink on the three apps that carry no Tailwind preset.
 */
export const Fullstop = ({ className }: { className?: string }) => (
  <span aria-hidden className={cn('hottr-wordmark__dot', className)}>
    .
  </span>
);
